import {toast} from '@cashiaApp/web-components';
import {useEffect, useRef, useState, useCallback} from 'react';

import {
  CardAuthRequest,
  cardPaymentAuth,
  MessageData,
  RawCheckoutData,
  TransactionResponse,
} from '../api';
import ProcessingPayment from './ProcessingPayment';
import {centinelUrl} from '../../../../utils/constants';

interface CardPaymentInlineProps {
  isVisible: boolean;
  transactionResponse: TransactionResponse | null;
  reference: string;
}

const CardPaymentInline: React.FC<CardPaymentInlineProps> = ({
  isVisible,
  transactionResponse,
  reference,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [stepUpProcessed, setStepUpProcessed] = useState(false);

  const stepUpFormRef = useRef<HTMLFormElement>(null);
  const stepUpJwtRef = useRef<HTMLInputElement>(null);
  const stepUpMdRef = useRef<HTMLInputElement>(null);

  const collectionFormRef = useRef<HTMLFormElement>(null);

  // Reset state when invisible or new transaction
  useEffect(() => {
    if (!isVisible) {
      setIsLoading(false);
      setStepUpProcessed(false);
    }
  }, [isVisible]);

  useEffect(() => {
    setStepUpProcessed(false);
  }, [transactionResponse?.form?.parameters?.accessToken]);

  const handleStepUpMessage = useCallback(
    async (event: MessageEvent) => {
      try {
        const message: MessageData =
          typeof event.data === 'string'
            ? (JSON.parse(event.data) as MessageData)
            : (event.data as MessageData);
        if (
          event.origin === centinelUrl &&
          message.MessageType === 'profile.completed' &&
          !stepUpProcessed
        ) {
          setStepUpProcessed(true);
          setIsLoading(true);

          // Fetch step-up data
          const requestBody: CardAuthRequest = {
            txId: transactionResponse?.transaction?.id || '',
          };
          const response: RawCheckoutData = await cardPaymentAuth(
            reference,
            requestBody
          );

          // Submit the step-up form
          if (
            stepUpFormRef.current &&
            stepUpJwtRef.current &&
            stepUpMdRef.current
          ) {
            stepUpJwtRef.current.value =
              response.consumerAuthenticationInformation.accessToken ?? '';
            stepUpFormRef.current.action =
              response.consumerAuthenticationInformation.stepUpUrl ?? '';
            setTimeout(() => stepUpFormRef.current?.submit(), 500);
          }

          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error processing StepUp message:', err);
        toast.error('An error occurred during payment processing.');
        setStepUpProcessed(false);
        setIsLoading(false);
      }
    },
    [reference, stepUpProcessed, transactionResponse?.transaction?.id]
  );

  useEffect(() => {
    if (!transactionResponse?.form?.url || !isVisible) return;

    const collectionForm = collectionFormRef.current;
    if (!collectionForm) return;

    setIsLoading(true);

    // 60s timeout in case of no response
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      console.warn('Cardinal form submission timed out');
    }, 60000);

    collectionForm.submit();
    window.addEventListener('message', handleStepUpMessage);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('message', handleStepUpMessage);
    };
  }, [transactionResponse?.form?.url, isVisible, handleStepUpMessage]);

  if (!isVisible) return null;

  return (
    <>
      {isLoading && <ProcessingPayment />}
      <div
        className="flex flex-col items-center justify-center w-full h-full min-h-fit overflow-auto"
        style={isLoading ? {width: 0, height: 0, overflow: 'hidden'} : {}}>
        <iframe
          id="cardinal_collection_iframe"
          name="collectionIframe"
          style={{display: 'none'}}
        />
        <form
          ref={collectionFormRef}
          id="cardinal_collection_form"
          method="POST"
          target="collectionIframe"
          action={transactionResponse?.form?.url ?? ''}>
          <input
            type="hidden"
            name="JWT"
            value={transactionResponse?.form?.parameters?.accessToken ?? ''}
          />
        </form>

        <iframe
          id="step-up-iframe"
          name="step-up-iframe"
          height={isLoading ? '0' : '750'}
          width="100%"
          style={{border: 'none'}}
        />
        <form
          ref={stepUpFormRef}
          id="step-up-form"
          target="step-up-iframe"
          method="POST">
          <input ref={stepUpJwtRef} type="hidden" name="JWT" />
          <input ref={stepUpMdRef} type="hidden" name="MD" />
        </form>
      </div>
    </>
  );
};

export default CardPaymentInline;
